<script>
    export const COMPOSER_COLLAPSED_HEIGHT_PX = 40

    export const COMPOSER_EXPANDED_MIN_HEIGHT_PX = 80

    /**
     * Collapsed: a short one-to-two row field. Expanded: at least the old fixed height, growing with the content;
     * the input section's max height (half the module) caps it and the textarea scrolls beyond that.
     */
    export function composerTextareaHeight({ expanded, contentHeight }) {
        if (!expanded) {
            return `${COMPOSER_COLLAPSED_HEIGHT_PX}px`
        }

        return `${Math.max(COMPOSER_EXPANDED_MIN_HEIGHT_PX, Number(contentHeight) || 0)}px`
    }

    /**
     * Each tour step shows only once its beforeStep hook has expanded the section, then v-onboarding scrolls the target
     * to the centre of the screen like the shared tour's default (the hook itself does not scroll).
     */
    export const TOUR_STEP_OPTIONS = {
        hideNextStepDuringHook: true,
        scrollToStep: { enabled: true, options: { behavior: 'smooth', block: 'center' } },
    }
</script>

<script setup>
    import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useRouter } from 'vue-router'
    import { useMediaQuery } from '@vueuse/core'
    import moment from 'moment'

    import { XMarkIcon } from '@heroicons/vue/20/solid'

    import ConfirmationModal from '@/Components/App/ConfirmationModal.vue'
    import Loader from '@/Components/App/Loader.vue'
    import LatteTimelineEntry, { editableText, eventForNotesHash, eventPreview } from '@/Components/Notes/LatteTimelineEntry.vue'
    import LatteRatingCircles, { FEEDBACK_RATINGS, RATING_BORDER_CLASSES } from '@/Components/Feedback/LatteRatingCircles.vue'
    import LatteSummary from '@/Components/Notes/LatteSummary.vue'

    import { expandElement } from '@/Helpers/util'
    import { notesSubject, useJobStore, useNotesStore, useViewStore } from '@/Stores'

    const props = defineProps({
        subject: {
            type: Object,
            required: true,
        },
        /**
         * Resolves with the persisted subject id, creating the subject first when it is not saved yet (Client.vue's
         * client creation gate). Only needed where the module is shown for an unsaved subject.
         */
        ensurePersisted: {
            type: Function,
            default: null,
        },
        /**
         * Fills the parent's height on desktop instead of the fixed 560px (the chromeless ClientNotes page).
         */
        fillHeight: {
            type: Boolean,
            default: false,
        },
        /**
         * The page's route hash, passed by Client.vue once it has scrolled the section into view: a timeline entry
         * permalink (`#notes-<unix seconds>`) focuses its entry after the timeline loads.
         */
        focusHash: {
            type: String,
            default: '',
        },
    })

    const HIGHLIGHT_DURATION = 1500

    const TIMELINE_TARGET = 'notes_timeline'

    const NOTE_COMPOSER_TARGET = 'notes_composer'

    /**
     * The collapsible container holding the composer (the Notes & Activity section on Client.vue).
     */
    const NOTE_COMPOSER_CONTAINER = 'notes'

    const SUMMARY_TARGET = 'notes_summary'

    const PLAIN_NOTE_RATING = 'none'

    const router = useRouter()

    /**
     * Entry permalinks always point at the client's Latte profile, also from the embedded Coffee notes page.
     */
    const profileUrl = computed(() => (props.subject.id ? window.location.origin + router.resolve({ name: 'client.profile', params: { client: props.subject.id } }).href : null))

    const notesStore = useNotesStore()
    const { events, loadingTimeline, savingNote, deletingNoteKey, updatingNoteKey, notAvailable } = storeToRefs(notesStore)

    const jobStore = useJobStore()
    const { sortedJobsByClientAll } = storeToRefs(jobStore)

    /**
     * The client's jobs as the page hydrated them into the job store (Client.vue); the module never fetches jobs,
     * so the dropdown fills in whenever the page finishes loading them.
     */
    const rateableJobs = computed(() => Object.values(sortedJobsByClientAll.value?.[props.subject.id] ?? {}))

    const newNoteText = ref('')
    const composerRating = ref(PLAIN_NOTE_RATING)
    const composerJobId = ref(null)

    const composerSection = ref(null)
    const composerTextarea = ref(null)
    const composerFocused = ref(false)

    const composerContentHeight = ref(0)

    /**
     * The composer stays open while it has focus or anything in it: text, a colour or a job.
     */
    const composerExpanded = computed(() => composerFocused.value || newNoteText.value.trim() !== '' || composerRating.value !== PLAIN_NOTE_RATING || composerJobId.value !== null)

    const composerHeight = computed(() => composerTextareaHeight({ expanded: composerExpanded.value, contentHeight: composerContentHeight.value }))

    /**
     * Measures the text's natural height without leaving the textarea collapsed, so the height transition runs from
     * the current height to the new one.
     */
    function measureComposer() {
        const el = composerTextarea.value
        if (!el) {
            return
        }
        const current = el.style.height
        el.style.height = '0px'
        composerContentHeight.value = el.scrollHeight
        el.style.height = current
    }

    watch(newNoteText, () => nextTick(measureComposer))

    function onComposerFocusIn() {
        composerFocused.value = true
    }

    /**
     * Focus moving into the composer's own controls (the circles or the dropdown) keeps it open.
     */
    function onComposerFocusOut(event) {
        if (event?.relatedTarget && composerSection.value?.contains(event.relatedTarget)) {
            return
        }
        if (viewStore.tourActive) {
            return
        }
        composerFocused.value = false
    }

    /**
     * While a tour runs, focus moving to its card leaves the composer open (onComposerFocusOut); leaving the composer
     * step matches the open state to where focus really is.
     */
    function syncComposerFocus() {
        composerFocused.value = Boolean(composerSection.value?.contains(document.activeElement))
    }

    /**
     * The client's rateable jobs (those with a caregiver) by caregiver name, newest start first.
     */
    const jobOptions = computed(() =>
        rateableJobs.value
            .filter((job) => job?.id && job?.caregiver)
            .sort((a, b) => (b.start_date ? Date.parse(b.start_date) : 0) - (a.start_date ? Date.parse(a.start_date) : 0))
            .map((job) => {
                const name = [job.caregiver.first_name, job.caregiver.last_name].filter(Boolean).join(' ') || 'Unknown caregiver'

                return { value: job.id, label: name }
            }),
    )
    const hasRateableJobs = computed(() => jobOptions.value.length > 0)
    const composerPlaceholder = computed(() => (hasRateableJobs.value ? 'Add a new note or caregiver feedback...' : 'Add a new note...'))
    const isFeedbackSelected = computed(() => composerRating.value !== PLAIN_NOTE_RATING)
    const composerBorderClass = computed(() => RATING_BORDER_CLASSES[composerRating.value] ?? 'border-stone-300')
    const hasComposerText = computed(() => newNoteText.value.trim() !== '')
    const disabledComposerRatings = computed(() => (hasRateableJobs.value ? [] : FEEDBACK_RATINGS))
    const persistingSubject = ref(false)
    const composerError = ref(null)
    const savingComposer = computed(() => savingNote.value || persistingSubject.value)
    const timelineEvents = computed(() => (persisted.value ? events.value : []))

    /**
     * Save rules: a plain note needs text; feedback needs text and a job (green included).
     */
    const canSave = computed(() => !savingComposer.value && hasComposerText.value && (!isFeedbackSelected.value || composerJobId.value !== null))
    const saveLabel = computed(() => (isFeedbackSelected.value ? 'Save feedback' : 'Save note'))
    const timelineContainer = ref(null)
    const highlightedKey = ref(null)

    const noteToDelete = ref(null)
    const editingNoteKey = ref(null)

    let highlightTimeout = null

    const viewStore = useViewStore()

    /**
     * The "?" tour (the shared v-onboarding tour, LatteOnboardingTour): timeline, composer, then summary. Each step
     * first expands the Notes & Activity section and v-onboarding then centres its target (TOUR_STEP_OPTIONS); on narrow
     * screens the timeline and composer steps open the full timeline and the summary step closes it. The composer step
     * focuses (and so expands) the composer.
     */
    const TOUR_STEPS = [
        {
            target: TIMELINE_TARGET,
            inTimeline: true,
            title: 'Timeline',
            description: "This client's notes, caregiver feedback and disposition changes, newest first. Hover a time to see the exact date; click it to copy a link to the entry.",
        },
        {
            target: NOTE_COMPOSER_TARGET,
            inTimeline: true,
            focusComposer: true,
            title: 'Add a note',
            description: 'Type a note and save it. Pick a red, yellow or green circle and a caregiver to record feedback on their job instead.',
        },
        {
            target: SUMMARY_TARGET,
            inTimeline: false,
            title: 'AI summary',
            description: 'Generated by AI from the timeline. It can be wrong or incomplete, so check important details against the linked timeline entries before acting on them.',
        },
    ]

    function startTour() {
        if (notAvailable.value) {
            return false
        }
        viewStore.startTour(
            TOUR_STEPS.map(({ target, inTimeline, focusComposer = false, title, description }) => ({
                attachTo: { element: () => document.getElementById(target) },
                content: { title, description },
                options: TOUR_STEP_OPTIONS,
                on: {
                    beforeStep: async () => {
                        timelineOpen.value = inTimeline
                        await nextTick()
                        const expanded = await expandElement(target, NOTE_COMPOSER_CONTAINER)
                        if (expanded && focusComposer) {
                            composerTextarea.value?.focus({ preventScroll: true })
                        }

                        return expanded
                    },
                    afterStep: focusComposer ? syncComposerFocus : undefined,
                },
            })),
        )

        return true
    }

    defineExpose({ startTour })

    /**
     * True while the first note is creating the subject: saveNote() stores the note and loads the module itself, so
     * the subject prop changing from unsaved to saved must not reload (and race the note) or clear the composer.
     */
    let creatingSubjectFromNote = false

    const persisted = computed(() => Boolean(props.subject.segment))

    /**
     * Set once the subject's timeline load settles, so a permalink is matched against loaded events, not the empty list.
     */
    const timelineLoaded = ref(false)

    watch(loadingTimeline, (loading, wasLoading) => {
        if (wasLoading && !loading) {
            timelineLoaded.value = true
        }
    })

    function load(subject) {
        if (!subject.segment) {
            return
        }
        timelineLoaded.value = false
        notesStore.setSubject(subject)
        notesStore.loadTimeline().catch(() => {})
        notesStore.loadSummary().catch(() => {})
    }

    function resetComposer() {
        newNoteText.value = ''
        composerRating.value = PLAIN_NOTE_RATING
        composerJobId.value = null
    }

    /**
     * On an unsaved subject the first note creates it (through `ensurePersisted`, the same path as other fields), then
     * the note is stored against the new id and the module loads as for a saved subject. If creation fails the text
     * stays in the composer and no note is stored.
     */
    async function persistSubjectForNote() {
        if (!props.ensurePersisted) {
            throw new Error('This profile is not saved yet.')
        }
        persistingSubject.value = true
        try {
            const subjectId = await props.ensurePersisted()
            const subject = notesSubject(subjectId)
            notesStore.setSubject(subject)

            return subject
        } finally {
            persistingSubject.value = false
        }
    }

    async function saveNote() {
        if (!canSave.value) {
            return
        }
        const text = newNoteText.value.trim()
        composerError.value = null

        let createdSubject = null
        if (!persisted.value) {
            try {
                creatingSubjectFromNote = true
                createdSubject = await persistSubjectForNote()
            } catch (e) {
                creatingSubjectFromNote = false
                composerError.value = 'The client could not be saved, so the note was not added. Please try again.'
                return
            }
        }

        let createdKey = null
        try {
            if (isFeedbackSelected.value) {
                const rating = await notesStore.addFeedback(composerJobId.value, composerRating.value, text)
                createdKey = rating?.id ? `feedback:${rating.id}` : null
            } else {
                const created = await notesStore.addNote(text)
                createdKey = created?.key ?? null
            }
            resetComposer()
        } catch (e) {
            // Error notification is handled by the store.
        } finally {
            if (createdSubject) {
                creatingSubjectFromNote = false
                load(createdSubject)
            }
        }

        if (createdKey) {
            await nextTick()
            scrollToEvent(createdKey)
        }
    }

    watch(newNoteText, () => {
        composerError.value = null
    })

    watch(composerRating, (rating) => {
        if (rating === PLAIN_NOTE_RATING) {
            composerJobId.value = null
        }
    })

    watch(hasRateableJobs, (hasJobs) => {
        if (!hasJobs) {
            composerRating.value = PLAIN_NOTE_RATING
        }
    })

    const deleteNoun = computed(() => (noteToDelete.value?.type === 'feedback' ? 'feedback' : 'note'))

    function requestDelete(event) {
        noteToDelete.value = event
    }

    function cancelDelete() {
        noteToDelete.value = null
    }

    async function confirmDelete() {
        const event = noteToDelete.value
        noteToDelete.value = null
        if (!event) {
            return
        }
        if (editingNoteKey.value === event.key) {
            editingNoteKey.value = null
        }

        try {
            if (event.type === 'feedback') {
                await notesStore.deleteFeedback(event)
            } else {
                await notesStore.deleteNote(event)
            }
        } catch (e) {
            // Error notification is handled by the store.
        }
    }

    function startEdit(event) {
        editingNoteKey.value = event?.key ?? null
    }

    function cancelEdit() {
        editingNoteKey.value = null
    }

    /**
     * Unchanged input just closes the editor; otherwise the editor stays open until the save succeeds.
     */
    async function saveEdit({ event, text, rating = null }) {
        const trimmed = (text ?? '').trim()
        if (!trimmed || updatingNoteKey.value) {
            return
        }
        const isFeedback = event.type === 'feedback'
        if (trimmed === editableText(event) && (!isFeedback || rating === event.data?.rating)) {
            editingNoteKey.value = null
            return
        }

        try {
            if (isFeedback) {
                await notesStore.updateFeedback(event, rating, trimmed)
            } else {
                await notesStore.updateNote(event, trimmed)
            }
            if (editingNoteKey.value === event.key) {
                editingNoteKey.value = null
            }
        } catch (e) {
            // Error notification is handled by the store.
        }
    }

    /**
     * Wide screens show the timeline beside the summary; narrow screens show the summary with a few recent events and
     * open the full timeline as a full-screen view on demand.
     */
    const RECENT_EVENT_COUNT = 3

    const isWideScreen = useMediaQuery('(min-width: 768px)')
    const timelineOpen = ref(false)
    const timelineTakeover = computed(() => timelineOpen.value && !isWideScreen.value)
    const recentEvents = computed(() => timelineEvents.value.slice(0, RECENT_EVENT_COUNT))
    const takeoverCloseButton = ref(null)

    let bodyOverflowBeforeLock = null

    function lockBodyScroll() {
        if (bodyOverflowBeforeLock !== null) {
            return
        }
        bodyOverflowBeforeLock = document.body.style.overflow
        document.body.style.overflow = 'hidden'
    }

    function unlockBodyScroll() {
        if (bodyOverflowBeforeLock === null) {
            return
        }
        document.body.style.overflow = bodyOverflowBeforeLock
        bodyOverflowBeforeLock = null
    }

    watch(timelineTakeover, (open) => {
        if (open) {
            lockBodyScroll()
            nextTick(() => takeoverCloseButton.value?.focus({ preventScroll: true }))
        } else {
            unlockBodyScroll()
        }
    })

    /**
     * Modal clears the body overflow when the delete confirmation closes; the open full timeline keeps the page locked.
     */
    watch(
        noteToDelete,
        (event) => {
            if (event === null && timelineTakeover.value) {
                document.body.style.overflow = 'hidden'
            }
        },
        { flush: 'post' },
    )

    watch(isWideScreen, (wide) => {
        if (wide) {
            timelineOpen.value = false
        }
    })

    async function openTimeline(eventKey = null) {
        timelineOpen.value = true
        await nextTick()
        if (eventKey) {
            scrollToEvent(eventKey)
        }
    }

    function closeTimeline() {
        timelineOpen.value = false
    }

    function recentEventTime(event) {
        return event.occurred_at ? moment(event.occurred_at).fromNow() : ''
    }

    /**
     * Summary links scroll the timeline beside the summary on wide screens and open the full timeline on narrow ones.
     */
    function navigateToEvent(eventKey) {
        if (!eventKey) {
            return
        }
        if (!isWideScreen.value) {
            return openTimeline(eventKey)
        }
        scrollToEvent(eventKey)
    }

    /**
     * Scrolls the timeline to an entry and highlights it briefly: used by summary links and for a newly saved note or
     * feedback (the newest entry, so the timeline ends up at the top).
     */
    function scrollToEvent(eventKey) {
        const container = timelineContainer.value
        if (!container) {
            return
        }

        const entry = Array.from(container.querySelectorAll('[data-event-key]')).find((element) => element.dataset.eventKey === eventKey)
        if (!entry) {
            return
        }
        entry.scrollIntoView({ block: 'center', behavior: 'smooth' })

        clearTimeout(highlightTimeout)
        highlightedKey.value = eventKey
        highlightTimeout = setTimeout(() => {
            highlightedKey.value = null
        }, HIGHLIGHT_DURATION)
    }

    let focusedHash = null

    /**
     * Focuses a permalinked entry like a summary link, once per hash and only after the timeline has loaded. With no
     * matching entry the section simply stays in view.
     */
    function focusEventFromHash() {
        const hash = props.focusHash
        if (!hash || hash === focusedHash || !timelineLoaded.value) {
            return
        }
        focusedHash = hash
        const event = eventForNotesHash(timelineEvents.value, hash)
        if (event) {
            navigateToEvent(event.key)
        }
    }

    watch(() => [props.focusHash, timelineLoaded.value], focusEventFromHash, { flush: 'post' })

    watch(
        () => props.subject?.segment,
        (segment, previousSegment) => {
            if (segment === previousSegment || creatingSubjectFromNote) {
                return
            }
            if (!previousSegment) {
                load(props.subject)
                return
            }
            resetComposer()
            timelineOpen.value = false
            highlightedKey.value = null
            noteToDelete.value = null
            editingNoteKey.value = null
            focusedHash = null
            load(props.subject)
        },
    )

    onMounted(() => {
        load(props.subject)
    })

    onUnmounted(() => {
        clearTimeout(highlightTimeout)
        unlockBodyScroll()
        notesStore.reset()
    })
</script>

<template>
    <div
        v-if="notAvailable"
        class="px-4 py-8 text-center font-sans text-sm text-zinc-500 md:px-6"
    >
        Notes &amp; Activity is not available for your account.
    </div>

    <div
        v-else
        class="flex flex-col gap-6 px-4 py-4 font-sans md:flex-row md:gap-0 md:px-6"
        :class="fillHeight ? 'md:h-full' : 'md:h-[560px]'"
    >
        <div
            data-test="timeline-panel"
            :role="timelineTakeover ? 'dialog' : undefined"
            :aria-modal="timelineTakeover ? 'true' : undefined"
            :aria-label="timelineTakeover ? 'Notes and activity timeline' : undefined"
            :class="['min-w-0 flex-col md:flex md:h-full md:w-3/5 md:pr-6', timelineTakeover ? 'fixed inset-0 z-fullscreen-takeover flex overflow-y-auto overscroll-contain bg-white' : 'hidden']"
            @keydown.esc="closeTimeline"
        >
            <div
                v-if="timelineTakeover"
                data-test="timeline-takeover-header"
                class="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-4 py-3"
            >
                <h3 class="text-sm font-semibold text-lifeworx-blue-800">Notes &amp; Activity</h3>
                <button
                    ref="takeoverCloseButton"
                    type="button"
                    data-test="timeline-takeover-close"
                    class="flex h-10 w-10 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400"
                    @click="closeTimeline"
                >
                    <span class="sr-only">Close timeline</span>
                    <XMarkIcon
                        class="h-6 w-6"
                        aria-hidden="true"
                    />
                </button>
            </div>

            <div class="flex flex-col gap-3 px-4 py-4 md:min-h-0 md:flex-1 md:p-0">
                <div
                    id="notes_composer"
                    ref="composerSection"
                    data-test="composer"
                    class="flex shrink-0 flex-col gap-2 md:max-h-[50%]"
                    @focusin="onComposerFocusIn"
                    @focusout="onComposerFocusOut"
                >
                    <textarea
                        ref="composerTextarea"
                        v-model="newNoteText"
                        data-test="new-note"
                        rows="1"
                        class="min-h-0 w-full shrink resize-none overflow-y-auto rounded-lg border px-3 py-2 text-sm transition-[height,border-color] duration-200 ease-out placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400"
                        :class="composerBorderClass"
                        :style="{ height: composerHeight }"
                        :placeholder="composerPlaceholder"
                        :disabled="savingComposer"
                        @keydown.meta.enter.prevent="saveNote"
                        @keydown.ctrl.enter.prevent="saveNote"
                    />
                    <div
                        v-if="composerExpanded"
                        data-test="composer-controls"
                        class="flex shrink-0 items-center gap-3"
                    >
                        <LatteRatingCircles
                            v-model="composerRating"
                            data-test="composer-rating"
                            show-group-labels
                            :disabled-ratings="disabledComposerRatings"
                            :disabled="savingComposer"
                            @mousedown.prevent
                        />
                        <label
                            for="composer-feedback-job"
                            class="sr-only"
                        >
                            Job to rate
                        </label>
                        <select
                            id="composer-feedback-job"
                            v-model="composerJobId"
                            data-test="feedback-job"
                            class="min-w-0 max-w-xs flex-1 rounded-md border border-stone-300 py-1.5 pl-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400"
                            :disabled="!isFeedbackSelected || savingComposer"
                        >
                            <option :value="null">{{ hasRateableJobs ? 'Select caregiver…' : 'No caregivers to rate' }}</option>
                            <option
                                v-for="option in jobOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                        <button
                            type="button"
                            data-test="save-composer"
                            class="ml-auto shrink-0 rounded-md bg-lifeworx-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-lifeworx-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="!canSave"
                            @click="saveNote"
                        >
                            {{ savingComposer ? 'Saving…' : saveLabel }}
                        </button>
                    </div>
                    <p
                        v-if="composerError"
                        data-test="composer-error"
                        role="alert"
                        class="text-sm text-lifeworx-red-500"
                    >
                        {{ composerError }}
                    </p>
                </div>

                <div
                    :id="TIMELINE_TARGET"
                    data-test="timeline"
                    class="relative md:min-h-0 md:flex-1"
                >
                    <div
                        ref="timelineContainer"
                        data-test="timeline-scroller"
                        class="pb-8 pr-1 md:h-full md:overflow-y-auto"
                    >
                        <div
                            v-if="loadingTimeline && timelineEvents.length === 0"
                            class="py-8"
                        >
                            <Loader />
                        </div>
                        <div
                            v-else-if="timelineEvents.length === 0"
                            class="py-8 text-center text-sm text-zinc-500"
                        >
                            No notes or activity yet.
                        </div>
                        <ol
                            v-else
                            data-test="timeline-list"
                            class="flex flex-col gap-5"
                        >
                            <LatteTimelineEntry
                                v-for="(event, index) in timelineEvents"
                                :key="event.key"
                                :event="event"
                                :highlighted="highlightedKey === event.key"
                                :is-first="index === 0"
                                :is-last="index === timelineEvents.length - 1"
                                :deleting="deletingNoteKey === event.key"
                                :editing="editingNoteKey === event.key"
                                :saving="updatingNoteKey === event.key"
                                :profile-url="profileUrl"
                                @delete="requestDelete"
                                @edit="startEdit"
                                @cancel-edit="cancelEdit"
                                @save="saveEdit"
                            />
                        </ol>
                    </div>
                    <div
                        class="pointer-events-none absolute inset-x-0 bottom-0 hidden h-8 bg-gradient-to-t from-white to-transparent md:block"
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>

        <div
            :id="SUMMARY_TARGET"
            data-test="summary-panel"
            class="min-w-0 md:h-full md:w-2/5 md:overflow-y-auto md:border-l md:border-zinc-200 md:pl-6"
        >
            <LatteSummary
                v-if="persisted"
                :subject="subject"
                :events="events"
                @navigate="navigateToEvent"
            />
            <p
                v-else
                data-test="summary-unsaved"
                class="text-sm text-zinc-500"
            >
                A summary appears once the client is saved.
            </p>

            <section
                data-test="recent-events"
                class="mt-6 border-t border-zinc-200 pt-4 md:hidden"
            >
                <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Recent activity</h3>
                <div
                    v-if="loadingTimeline && recentEvents.length === 0"
                    class="py-4"
                >
                    <Loader />
                </div>
                <p
                    v-else-if="recentEvents.length === 0"
                    data-test="recent-events-empty"
                    class="text-sm text-zinc-500"
                >
                    No notes or activity yet.
                </p>
                <ol
                    v-else
                    class="flex flex-col gap-2"
                >
                    <li
                        v-for="event in recentEvents"
                        :key="event.key"
                    >
                        <button
                            type="button"
                            data-test="recent-event"
                            :data-recent-key="event.key"
                            class="w-full rounded-lg border border-zinc-200 px-3 py-2 text-left hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400"
                            @click="openTimeline(event.key)"
                        >
                            <span class="line-clamp-2 break-words text-sm text-lifeworx-blue-800">{{ eventPreview(event) }}</span>
                            <span class="mt-1 block text-xs text-zinc-500">
                                {{ [event.author?.name, recentEventTime(event)].filter(Boolean).join(' · ') }}
                            </span>
                        </button>
                    </li>
                </ol>
                <button
                    type="button"
                    data-test="open-timeline"
                    class="mt-3 w-full rounded-md bg-lifeworx-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-lifeworx-blue-700 focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400"
                    @click="openTimeline()"
                >
                    View full timeline &amp; add notes
                </button>
            </section>
        </div>

        <ConfirmationModal
            :show="noteToDelete !== null"
            :title="`Delete ${deleteNoun}?`"
            :message="`The ${deleteNoun} will be removed from the timeline.`"
            action-label="Delete"
            :action-is-danger="true"
            :layer-class="timelineTakeover ? 'z-[12500]' : undefined"
            @confirm="confirmDelete"
            @close="cancelDelete"
        />
    </div>
</template>
